import { useState, useEffect, useCallback, useRef, createContext, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authService } from '../api';
import { invalidateSubOrderListByIdsCache } from '../api/services/subOrderListByIdsCache';
import { invalidateSubOrderListByIdsWithoutNameCache } from '../api/services/subOrderListByIdsWithoutNameCache';
import { LOCAL_STORAGE_KEYS } from '../config/appConstants';
import tokenService from '../utils/tokenService';
import Logger from '../utils/Logger';
import globalCacheManager from '../utils/cache/globalCacheManager';
import { clearAllSearchStates } from '../utils/searchStateCache';

const TOKEN_EXPIRY_CHECK_INTERVAL_MS = 60 * 1000; // 1분마다 만료 여부 확인

const AuthContext = createContext(null);

const isWorkerPagePath = () => {
  if (typeof window === 'undefined') return false;
  const pathname = window.location.pathname || '';
  return pathname === '/dyeing-worker' || pathname.startsWith('/dyeing-worker/');
};

export const AuthProvider = ({ children }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const checkAuthState = async () => {
      const token = await tokenService.getToken();
      if (!token) {
        setIsAuthenticated(false);
        setLoading(false);
        return;
      }

      // 작업자 페이지: getCurrentUser 검사 생략. 토큰 제거하지 않음(API 요청에 사용됨).
      if (isWorkerPagePath()) {
        setIsAuthenticated(false);
        setLoading(false);
        return;
      }

      // expire_time_at_utc 기준 만료 시 세션 종료 후 로그인 화면으로
      if (tokenService.isTokenExpired && tokenService.isTokenExpired()) {
        tokenService.removeToken();
        globalCacheManager.cleanup().catch(() => {});
        localStorage.setItem('sessionExpired', 'true');
        setIsAuthenticated(false);
        setLoading(false);
        window.location.href = '/';
        return;
      }

      try {
        const result = await authService.getCurrentUser();
        if (result.success) {
          // Bundle(권한 포함) 준비 후에 인증 플래그를 올려 메뉴가 빈 권한으로 그려지지 않게 함
          const isPageRefresh = () => {
            if (typeof window === 'undefined' || typeof performance === 'undefined') {
              return false;
            }
            if (performance.navigation && performance.navigation.type === 1) {
              return true;
            }
            if (performance.getEntriesByType) {
              const navEntries = performance.getEntriesByType('navigation');
              if (navEntries.length > 0) {
                return navEntries[0].type === 'reload';
              }
            }
            return false;
          };

          try {
            await globalCacheManager.initialize(isPageRefresh());
          } catch (error) {
            console.error('Failed to initialize global cache on auth check:', error);
          }

          setUser(result.data);
          setIsAuthenticated(true);
          // PermissionContext 리스너 등록 후 권한 재전달
          setTimeout(() => {
            globalCacheManager.republishPermissions();
          }, 0);

          if (result.data.language) {
            i18n.changeLanguage(result.data.language);
          }

          // 테마 및 색상 설정 적용
          if (result.data.custom_style) {
            const { theme, color } = result.data.custom_style;
            if (theme && theme !== 'default') {
              localStorage.setItem('user_theme', theme);
            } else {
              localStorage.removeItem('user_theme');
            }
            if (color && color !== 'default') {
              localStorage.setItem('user_color', color);
            } else {
              localStorage.removeItem('user_color');
            }
            window.dispatchEvent(new CustomEvent('themeChanged', {
              detail: { theme: theme || 'default', color: color || 'default' }
            }));
          }
        } else {
          console.error('Auth check failed');
          if (!isWorkerPagePath()) tokenService.removeToken();
          setIsAuthenticated(false);
          // 인증 실패 시 캐시 정리 (작업자 페이지가 아닐 때만)
          if (!isWorkerPagePath()) globalCacheManager.cleanup().catch(() => {});
        }
      } catch (error) {
        console.error('Auth check error:', error);
        if (!isWorkerPagePath()) tokenService.removeToken();
        setIsAuthenticated(false);
        if (!isWorkerPagePath()) globalCacheManager.cleanup().catch(() => {});
      } finally {
        setLoading(false);
      }
    };

    checkAuthState();

    const sessionExpired = localStorage.getItem('sessionExpired');
    if (sessionExpired) {
      setError('sessionExpired');
      localStorage.removeItem('sessionExpired');
    }
  }, [i18n]);

  const login = useCallback(async (username, password, rememberMe, showAlert) => {
    setLoading(true);
    setError(null);

    try {
      const response = await authService.login(username, password);

      if (response.success) {
        const currentLanguage = i18n.language;

        if (rememberMe) {
          localStorage.setItem(LOCAL_STORAGE_KEYS.USERNAME, username);
          localStorage.setItem("user_id", response.data.user_id);
          localStorage.setItem(LOCAL_STORAGE_KEYS.LANGUAGE, currentLanguage);
        } else {
          localStorage.removeItem(LOCAL_STORAGE_KEYS.USERNAME);
          localStorage.removeItem(LOCAL_STORAGE_KEYS.LANGUAGE);
        }

        const userResult = await authService.getCurrentUser();
        if (userResult.success) {
          const userData = userResult.data;

          // localStorage에 사용자 정보 저장 (setUser 전에)
          localStorage.setItem("user_id", userData.user_id);

          if (userData.name) {
            localStorage.setItem(LOCAL_STORAGE_KEYS.USER_NAME, userData.name);
          }

          if (userData.department) {
            localStorage.setItem(LOCAL_STORAGE_KEYS.DEPARTMENT, userData.department.toLowerCase());
          }

          try {
            await authService.updateLanguage(currentLanguage);
          } catch (error) {
            console.error('Failed to update language setting:', error);
          }

          // 테마 및 색상 설정 적용
          if (userData.custom_style) {
            const { theme, color } = userData.custom_style;
            if (theme && theme !== 'default') {
              localStorage.setItem('user_theme', theme);
            } else {
              localStorage.removeItem('user_theme');
            }
            if (color && color !== 'default') {
              localStorage.setItem('user_color', color);
            } else {
              localStorage.removeItem('user_color');
            }
            // 테마 변경 이벤트 발생
            window.dispatchEvent(new CustomEvent('themeChanged', {
              detail: { theme: theme || 'default', color: color || 'default' }
            }));
          }

          // Bundle 기반 전역 캐시 초기화 (완료 대기 → PermissionContext가 준비된 권한 사용)
          try {
            await globalCacheManager.initialize(true);
          } catch (error) {
            console.error('Failed to initialize global cache:', error);
          }

          // 캐시가 준비된 후에 인증 상태 설정 및 사용자 정보 설정
          setIsAuthenticated(true);
          setUser(userData);
          setTimeout(() => {
            globalCacheManager.republishPermissions();
          }, 0);
        }

        return true;
      }

      handleLoginError(response.error, showAlert);
      return false;
    } catch (error) {
      console.error('Login error: ', error);
      handleLoginError(error, showAlert);
      return false;
    } finally {
      setLoading(false);
    }
  }, [i18n]);

  const handleLoginError = (error, showAlert) => {
    let errorMessage = error.message || 'unknown';

    if (error.message === 'waiting_approval') {
      errorMessage = 'waitingApproval';
    } else if (error.message === 'blocked') {
      errorMessage = 'blocked';
    } else if (error.message === 'Account does not exist' || error.code === 404) {
      errorMessage = 'notFound';
    } else if (error.message === 'Invalid password' || error.code === 401) {
      errorMessage = 'invalidPassword';
    } else if (error.message === 'network' || error.message === 'Network Error') {
      errorMessage = 'network';
    }

    setError(errorMessage);

    const translatedError = t(`login.error.${errorMessage}`);

    if (showAlert && typeof showAlert === 'function') {
      showAlert(translatedError);
    }
  };

  const logout = useCallback(async () => {
    try {
      const currentLanguage = i18n.language;
      await authService.updateLanguage(currentLanguage);
    } catch (error) {
      console.error('Failed to update language on logout:', error);
    }

    // 로그아웃 시 전역 캐시 정리
    await globalCacheManager.cleanup();
    invalidateSubOrderListByIdsCache();
    invalidateSubOrderListByIdsWithoutNameCache();
    clearAllSearchStates();

    tokenService.removeToken();
    localStorage.removeItem(LOCAL_STORAGE_KEYS.USER_NAME);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.DEPARTMENT);
    setIsAuthenticated(false);
    setUser(null);
    navigate('/');
  }, [navigate, i18n]);

  // 로그인 유지 중 expire_time_at_utc 만료 시 자동 로그아웃 (logout 정의 이후에 배치)
  const logoutRef = useRef(logout);
  logoutRef.current = logout;
  useEffect(() => {
    if (!isAuthenticated) return;
    const checkExpiryAndLogout = () => {
      if (tokenService.isTokenExpired && tokenService.isTokenExpired()) {
        localStorage.setItem('sessionExpired', 'true');
        logoutRef.current?.();
      }
    };
    checkExpiryAndLogout(); // 마운트 시 한 번 확인
    const intervalId = setInterval(checkExpiryAndLogout, TOKEN_EXPIRY_CHECK_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [isAuthenticated]);

  const value = {
    isAuthenticated,
    user,
    loading,
    error,
    login,
    logout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};